import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Tags, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const tl = (v) => `₺${Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const iso = (d) => d.toISOString().slice(0, 10);

export const ExpenseCategorySelect = ({ categories, value, onChange, id = "expense_category" }) => (
  <div>
    <Label htmlFor={id}>Gider Kategorisi *</Label>
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger id={id} data-testid={`${id}-select`}><SelectValue placeholder="Kategori seçin (zorunlu)" /></SelectTrigger>
      <SelectContent>
        {categories.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`${id}-option-${c.id}`}>{c.name}</SelectItem>)}
      </SelectContent>
    </Select>
    {categories.length === 0 && <p className="text-xs text-red-600 mt-1">Önce "Kategoriler" butonundan kategori ekleyin.</p>}
  </div>
);

export const ExpenseCategoryManager = ({ api, sessionToken, categories, onChanged }) => {
  const [name, setName] = useState("");
  const add = async () => {
    if (!name.trim()) return;
    try {
      await axios.post(`${api}/expense-categories`, { name: name.trim() }, { params: { session_token: sessionToken } });
      toast.success("Kategori eklendi");
      setName("");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kategori eklenemedi");
    }
  };
  const remove = async (cat) => {
    if (!window.confirm(`"${cat.name}" kategorisi silinsin mi?`)) return;
    try {
      await axios.delete(`${api}/expense-categories/${cat.id}`, { params: { session_token: sessionToken } });
      toast.success("Kategori silindi");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kategori silinemedi");
    }
  };
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="expense-categories-btn"><Tags className="h-4 w-4 mr-2" /> Kategoriler</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="expense-categories-dialog">
        <DialogHeader><DialogTitle>Gider Kategorileri</DialogTitle></DialogHeader>
        <div className="flex gap-2">
          <Input placeholder="Yeni kategori adı (ör. Kira, Yakıt, Maaş)" value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} data-testid="expense-category-name-input" />
          <Button onClick={add} data-testid="expense-category-add-btn"><Plus className="h-4 w-4 mr-1" /> Ekle</Button>
        </div>
        <div className="divide-y border rounded-md max-h-72 overflow-y-auto">
          {categories.length === 0 && <p className="p-3 text-sm text-gray-500">Henüz kategori yok</p>}
          {categories.map((c) => (
            <div key={c.id} className="flex items-center justify-between p-2" data-testid={`expense-category-row-${c.id}`}>
              <span className="text-sm">{c.name} <span className="text-xs text-gray-400">({c.expense_count} kayıt)</span></span>
              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(c)} data-testid={`expense-category-delete-${c.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const periodRange = (period, monthValue) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (period) {
    case "this_month": return [iso(new Date(Date.UTC(y, m, 1))), iso(new Date(Date.UTC(y, m + 1, 1)))];
    case "last_month": return [iso(new Date(Date.UTC(y, m - 1, 1))), iso(new Date(Date.UTC(y, m, 1)))];
    case "this_year": return [`${y}-01-01`, `${y + 1}-01-01`];
    case "last_year": return [`${y - 1}-01-01`, `${y}-01-01`];
    case "month": {
      if (!monthValue) return ["", ""];
      const [yy, mm] = monthValue.split("-").map(Number);
      return [iso(new Date(Date.UTC(yy, mm - 1, 1))), iso(new Date(Date.UTC(yy, mm, 1)))];
    }
    default: return ["", ""];
  }
};

export const ExpenseFilterBar = ({ api, categories, onChange, refreshKey }) => {
  const [period, setPeriod] = useState("all");
  const [monthValue, setMonthValue] = useState("");
  const [categoryId, setCategoryId] = useState("all");
  const [summary, setSummary] = useState([]);

  useEffect(() => {
    const [start_date, end_date] = periodRange(period, monthValue);
    onChange({ start_date, end_date, category_id: categoryId === "all" ? "" : categoryId });
    axios.get(`${api}/expenses/category-summary`, { params: { start_date, end_date } })
      .then((r) => setSummary(r.data)).catch((e) => console.error(e));
  }, [period, monthValue, categoryId, refreshKey]);

  const max = Math.max(1, ...summary.map((s) => s.total));
  const total = summary.reduce((s, x) => s + x.total, 0);
  return (
    <div className="border rounded-lg p-4 bg-white space-y-4" data-testid="expense-filter-bar">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Dönem</Label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40" data-testid="expense-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Zamanlar</SelectItem>
              <SelectItem value="this_month">Bu Ay</SelectItem>
              <SelectItem value="last_month">Geçen Ay</SelectItem>
              <SelectItem value="this_year">Bu Yıl</SelectItem>
              <SelectItem value="last_year">Geçen Yıl</SelectItem>
              <SelectItem value="month">Ay Seç</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {period === "month" && (
          <div>
            <Label className="text-xs">Ay</Label>
            <Input type="month" value={monthValue} onChange={(e) => setMonthValue(e.target.value)} className="w-44" data-testid="expense-month-input" />
          </div>
        )}
        <div>
          <Label className="text-xs">Kategori</Label>
          <Select value={categoryId} onValueChange={setCategoryId}>
            <SelectTrigger className="w-48" data-testid="expense-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Kategoriler</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              <SelectItem value="uncategorized">Kategorisiz</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto text-right">
          <p className="text-xs text-gray-500">Dönem Toplamı</p>
          <p className="text-xl font-bold text-red-600" data-testid="expense-period-total">{tl(total)}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2" data-testid="expense-category-summary">
        {summary.map((s) => (
          <button key={s.category_id} type="button" onClick={() => setCategoryId(categoryId === s.category_id ? "all" : s.category_id)}
            className={`text-left rounded px-2 py-1 transition-colors ${categoryId === s.category_id ? "bg-red-50 ring-1 ring-red-300" : "hover:bg-gray-50"}`}
            data-testid={`expense-summary-${s.category_id}`}>
            <div className="flex justify-between text-sm">
              <span className="font-medium">{s.category_name} <span className="text-xs text-gray-400">({s.count})</span></span>
              <span>{tl(s.total)} • %{Math.round((s.total / (total || 1)) * 1000) / 10}</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1"><div className="bg-red-500 h-1.5 rounded-full" style={{ width: `${(s.total / max) * 100}%` }} /></div>
          </button>
        ))}
        {summary.length === 0 && <p className="text-sm text-gray-500">Bu dönemde gider yok</p>}
      </div>
    </div>
  );
};
